import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Gift, Heart } from 'lucide-react';

export default function ArtikelBedeutungEidUlAdha() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]">
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"><ArrowLeft className="w-4 h-4" />Alle Ratgeber</Link>
          <div className="flex items-center gap-2 text-[#D4C5A9] text-sm mb-4"><Calendar className="w-4 h-4" /><span>Februar 2026</span></div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight">Die Bedeutung von Eid ul Adha: Geschichte, Rituale und Bräuche</h1>
          <p className="text-lg text-white/80 leading-relaxed">Alles über das Opferfest – warum Muslime es feiern, welche Rituale dazugehören und wie es in Deutschland begangen wird.</p>
        </div>
      </section>
      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 prose prose-lg max-w-none">
          <p className="text-xl text-[#2C332E] leading-relaxed mb-8">Eid ul Adha (das Opferfest) ist eines der zwei wichtigsten Feste im Islam. Es wird weltweit von über 1,8 Milliarden Muslimen gefeiert und fällt auf den zehnten Tag des islamischen Monats Dhul-Hiddscha. Doch was steckt dahinter? Welche Geschichte wird erzählt, und wie wird das Fest heute in Deutschland gefeiert?</p>

          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center"><Heart className="w-6 h-6 text-[#4A5D4F]" /></div>
              <h2 className="font-serif text-2xl font-medium text-[#2C332E]">Die Geschichte von Ibrahim (a.s.)</h2>
            </div>
            <p className="text-[#4B5563] mb-4">Eid ul Adha erinnert an die Geschichte des Propheten Ibrahim (Abraham), der von Allah geprüft wurde. Im Traum erhielt Ibrahim den Befehl, seinen geliebten Sohn Ismail als Opfer darzubringen.</p>
            <div className="space-y-4">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Die Prüfung</h4>
                <p className="text-sm text-[#4B5563]">Ibrahim sprach mit seinem Sohn Ismail über den Traum. Ismail antwortete: "Oh Vater, tu was dir befohlen wird. Du wirst mich, so Allah will, geduldig finden." (Quran 37:102) Beide zeigten vollkommene Hingabe an Allahs Willen.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Die göttliche Erlösung</h4>
                <p className="text-sm text-[#4B5563]">Als Ibrahim bereit war, sein Opfer zu bringen, sandte Allah einen Widder als Ersatz. Die Prüfung war bestanden – es ging nie um das tatsächliche Opfer, sondern um die Bereitschaft, Allahs Willen zu folgen.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Die Lektion</h4>
                <p className="text-sm text-[#4B5563]">Eid ul Adha erinnert uns an Opferbereitschaft, Vertrauen in Allah und die Bereitschaft, das Liebste für einen höheren Zweck aufzugeben. Es geht nicht um Blut und Fleisch, sondern um die Hingabe des Herzens.</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Die Rituale zu Eid ul Adha</h2>
            <div className="space-y-4">
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Das Eid-Gebet</h4>
                <p className="text-sm text-[#4B5563]">Am Morgen des Festes versammeln sich Muslime zum gemeinschaftlichen Eid-Gebet. Man zieht die beste Kleidung an, geht einen anderen Weg zur Moschee als zurück und ruft Takbir ("Allahu Akbar").</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Das Opfer (Qurbani)</h4>
                <p className="text-sm text-[#4B5563]">Wer es sich leisten kann, opfert ein Tier (Schaf, Ziege, Rind oder Kamel). Das Fleisch wird in drei Teile aufgeteilt: ein Drittel für die Familie, ein Drittel für Freunde und Nachbarn, ein Drittel für Bedürftige.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Qurbani in Deutschland</h4>
                <p className="text-sm text-[#4B5563]">In Deutschland wird das Opfer meist über islamische Organisationen abgewickelt, die das Tier in zugelassenen Schlachthöfen schächten lassen. Viele Muslime spenden auch an Hilfsorganisationen, die in ärmeren Ländern schlachten und verteilen.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Familienbesuche & Geschenke</h4>
                <p className="text-sm text-[#4B5563]">Nach dem Gebet besucht man Verwandte und Freunde, tauscht Geschenke aus und genießt gemeinsam festliche Mahlzeiten. Kinder bekommen Eidi (Geldgeschenke) und neue Kleidung.</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Eid ul Adha und die Hajj</h2>
            <div className="space-y-4">
              <p className="text-[#4B5563]">Eid ul Adha fällt in die Zeit der Hajj – der großen Pilgerfahrt nach Mekka.</p>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Die Verbindung</h4>
                <p className="text-sm text-[#4B5563]">Während Millionen von Pilgern in Mekka stehen und zu Allah beten, feiern Muslime weltweit solidarisch mit ihnen. Der Tag von Arafat (9. Dhul-Hiddscha) ist der Höhepunkt der Hajj – das Fasten an diesem Tag wird besonders empfohlen.</p>
              </div>
              <div className="bg-[#F5F5F0] rounded-xl p-5">
                <h4 className="font-medium text-[#2C332E] mb-2">Wann ist Eid ul Adha 2026?</h4>
                <p className="text-sm text-[#4B5563]">Voraussichtlich am 27. Mai 2026 (10. Dhul-Hiddscha 1447). Das Fest dauert traditionell vier Tage.</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Eid ul Adha in Deutschland feiern</h2>
            <div className="bg-[#4A5D4F]/10 rounded-xl p-6">
              <ul className="text-[#4B5563] space-y-3">
                <li><strong>Gemeinsames Eid-Gebet:</strong> In Moscheen oder angemieteten Hallen – eine besondere Atmosphäre</li>
                <li><strong>Festliches Essen:</strong> Lamm, Reis, Salate und viele Süßigkeiten</li>
                <li><strong>Qurbani-Spende:</strong> Über Organisationen wie Islamic Relief, Muslim Aid oder lokale Moscheen</li>
                <li><strong>Geschenke austauschen:</strong> Besonders für Kinder – Spielzeug, Bücher, Eidi</li>
                <li><strong>Nachbarn einbeziehen:</strong> Fleisch und Süßigkeiten mit nicht-muslimischen Nachbarn teilen</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-serif text-2xl font-medium text-[#2C332E] mb-6">Fazit</h2>
            <p className="text-[#4B5563] leading-relaxed mb-4">Eid ul Adha erinnert uns an das Wichtigste: Vertrauen, Opferbereitschaft und Gemeinschaft. Es ist ein Fest der Großzügigkeit – gegenüber der Familie, den Nachbarn und den Bedürftigen dieser Welt. Möge dieses Wissen dazu beitragen, die Schönheit dieses Festes zu verstehen und zu schätzen.</p>
            <p className="text-xl text-[#4A5D4F] font-medium">Eid Mubarak!</p>
          </section>

          <div className="bg-[#F5F5F0] rounded-2xl p-6 mb-8">
            <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-4">Weitere Artikel</h3>
            <ul className="space-y-2">
              <li><Link to="/ratgeber/eid-ul-adha-geschenkideen" className="text-[#4A5D4F] hover:underline">→ Eid ul Adha Geschenkideen</Link></li>
              <li><Link to="/ratgeber/was-ist-eid" className="text-[#4A5D4F] hover:underline">→ Was ist Eid? Ein Leitfaden</Link></li>
            </ul>
          </div>

          <div className="bg-[#4A5D4F] rounded-2xl p-8 text-center">
            <h3 className="font-serif text-2xl font-medium text-white mb-4">Geschenke entdecken</h3>
            <Link to="/geschenke" className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-8 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors">Jetzt stöbern<Gift className="w-5 h-5" /></Link>
          </div>
        </div>
      </article>
    </main>
  );
}
